/**
 * "Importer fra PDF" — writer-only entry point for the pdf-import flow
 * (pdf-import, Slice 3b; API contract from Slice 2). ONE uploaded PDF (e.g. a
 * scanned cookbook) may contain MANY recipes — the backend parses + PERSISTS
 * all of them directly into the active cookbook, each flagged
 * `reviewState: 'needs_review'`.
 *
 * UNLIKE `PhotoImportButton`/`UrlImportForm`/`VideoImportButton`, this
 * component does NOT hand back an unsaved draft for the editor — there is no
 * single recipe to review-before-save here, there are potentially many
 * already-saved ones. So `onImported` here is a simple "I'm done, N recipes
 * landed" notification: the caller (`HomePage`) uses it to refetch the feed
 * and surface the new "Til gjennomgang" filter, NOT to navigate anywhere.
 *
 * PREMIUM, UP FRONT: PDF import is a paid capability, so the toggle refuses
 * BEFORE the modal (and therefore the file picker) opens when the active
 * cookbook has no Premium capabilities (`isPremium` — the OWNER's plan governs
 * the cookbook, §2.1). Letting the user find and pick a PDF and only THEN
 * saying "that needs Premium" reads as a failure (issue #19). The toggle stays
 * VISIBLE and enabled on purpose — the user should learn the feature exists —
 * it just answers with the same calm `PremiumNotice` the server's 402
 * produces. The 402 handling below REMAINS: this gate is UX, not a security
 * boundary, and the plan can change between page load and click.
 *
 * UI shape: a small modal (mirrors `VideoImportButton`), but simpler — a
 * single file input (no url mode, no ingredient textarea): pick a .pdf →
 * validate type+size client-side (`validatePdfFile`) → request a presigned
 * upload url → PUT the bytes (no bearer token, exactly like the video/photo
 * upload) → start the job → poll with phase progress → on success, show a
 * brief confirmation ("Importerte N oppskrifter til gjennomgang") and let the
 * user close the modal themselves (no auto-navigate to interrupt).
 *
 * Errors: the usual `UnauthorizedError`/`ForbiddenError`/`NotFoundError`
 * mapping (mirrors `VideoImportButton`). A genuine server `"failed"` job
 * (`ImportJobFailedError` — e.g. NOT_A_PDF, TOO_LARGE, NO_RECIPE_FOUND) shows
 * the server's own `serverMessage`, plus — when present — its
 * `errorWorkaround`. There's no file-switch fallback action here (a PDF has
 * exactly one input mode).
 *
 * `ImportPollTimeoutError` is handled DIFFERENTLY from every other import
 * flow: a pdf job can legitimately run for minutes (up to `PDF_MAX_PAGES`
 * pages, chunked into many sequential Gemini calls), so the client-side poll
 * budget is raised (`PDF_IMPORT_MAX_POLL_ATTEMPTS`) — but even that can be
 * exhausted by an unusually large PDF while the job is still happily running
 * server-side. Rendering that as a red error with a "Prøv igjen" retry would
 * be actively harmful: retrying re-uploads and re-imports, and the
 * still-running original job would ALSO land its recipes — a guaranteed
 * duplicate. So a poll timeout instead shows a neutral "continues in the
 * background" notice (no retry affordance) and still calls `onImported` so
 * the caller refetches the feed / surfaces "Til gjennomgang" for whatever
 * has already landed.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  EntitlementRequiredError,
  ForbiddenError,
  NotFoundError,
  UnauthorizedError,
  requestPdfImportUpload,
  startPdfImport,
} from "../api";
import { PremiumNotice } from "./PremiumNotice";
import { type EntitlementDenial, isEntitlementDenial } from "../lib/entitlement";
import {
  DEFAULT_POLL_INTERVAL_MS,
  ImportJobFailedError,
  ImportPollCancelledError,
  ImportPollTimeoutError,
  PDF_IMPORT_MAX_POLL_ATTEMPTS,
  type PdfImportResult,
  pollPdfImportJob,
} from "../lib/importPoll";
import { PDF_CONTENT_TYPE, validatePdfFile } from "../lib/pdfUpload";

/** Norwegian label for the pdf job's advisory `phase` (pdf-import only —
 * `null`/absent while the job hasn't reported one yet, or a `processing:k/M`
 * chunk progress once it has). */
function phaseLabel(phase: string | null | undefined): string {
  if (phase === "acquiring") return "Henter PDF …";
  if (phase === "structuring") return "Setter sammen oppskrifter …";
  if (phase?.startsWith("processing")) {
    const match = /^processing:(\d+)\/(\d+)$/.exec(phase);
    if (match) return `Leser side-bolk ${match[1]} av ${match[2]} …`;
    return "Leser PDF-en …";
  }
  return "Importerer PDF …";
}

export function PdfImportButton({
  cookbookId,
  isPremium,
  accessToken,
  onSessionExpired,
  onImported,
  toggleClassName = "sjef-import-btn",
  toggleLabel = "Importer fra PDF",
  testId = "pdf-import",
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxPollAttempts = PDF_IMPORT_MAX_POLL_ATTEMPTS,
  onBusyChange,
}: {
  cookbookId: string;
  /**
   * Whether the ACTIVE COOKBOOK has Premium capabilities
   * (`CookbookSummary.isPremium` from `/me`, via the cookbook context). NOT
   * "is the caller premium" — §2.1: the owner's plan governs the cookbook.
   * Required (no default) so a new call site cannot silently skip the gate.
   * A UI hint only — the server re-checks and answers 402.
   */
  isPremium: boolean;
  accessToken: string;
  onSessionExpired: () => void;
  /** Called once the import job is done — the caller refetches the feed and
   * surfaces the "Til gjennomgang" filter. Not a review-before-save draft. */
  onImported: (result: PdfImportResult) => void;
  toggleClassName?: string;
  toggleLabel?: React.ReactNode;
  testId?: string;
  /** Injectable for tests — how often to poll the job status. */
  pollIntervalMs?: number;
  /** Injectable for tests — how many polls before giving up client-side. */
  maxPollAttempts?: number;
  /** Reports whether an import is in flight, so a host sheet can refuse to
   * close (and so discard it) meanwhile. */
  onBusyChange?: (busy: boolean) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!busy) return;
    onBusyChange?.(true);
    return () => onBusyChange?.(false);
  }, [busy]);
  const [phase, setPhase] = useState<string | null>(null);
  // A string (the usual short inline error) OR a denial — either the parsed
  // 402 or the client's own up-front `isPremium` refusal. One state keeps them
  // mutually exclusive.
  const [error, setError] = useState<string | EntitlementDenial | null>(null);
  const [errorWorkaround, setErrorWorkaround] = useState<string | null>(null);
  const [result, setResult] = useState<PdfImportResult | null>(null);
  // Set only on a CLIENT-SIDE poll timeout (`ImportPollTimeoutError`) — the
  // job is very likely still running server-side, so this is deliberately
  // NOT rendered as an error: no red text, no retry affordance (a retry
  // would double-import once the still-running job also lands its
  // recipes). See the module docs above.
  const [timedOutInBackground, setTimedOutInBackground] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Same race-safety pattern as VideoImportButton: a run token, checked
  // before acting on an async result, so an unmount or a newer submit never
  // lets a stale poll loop call setState/onImported after the fact.
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * A cookbook switch must not leave the previous cookbook's answer on screen.
   * `HomePage` keeps the SAME component instance across a switch (no `key`),
   * so without this the Premium notice from a Free cookbook would still sit
   * under a button that now works. `isPremium` is in the deps too: a `/me`
   * refresh that flips the plan is the same situation.
   */
  useEffect(() => {
    setError(null);
  }, [cookbookId, isPremium]);

  /**
   * The ONE way the modal is opened. Without Premium capabilities we never
   * open it — the user would otherwise go find a PDF for an action that was
   * never available — and answer with the same notice as the 402 instead.
   */
  function openModal(): void {
    if (!isPremium) {
      setError({ code: "pdf_import" });
      return;
    }
    setError(null);
    setOpen(true);
  }

  function resetForm(): void {
    setFile(null);
    setError(null);
    setErrorWorkaround(null);
    setPhase(null);
    setResult(null);
    setTimedOutInBackground(false);
  }

  function closeModal(): void {
    setOpen(false);
    resetForm();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = ""; // allow re-picking the same file
    if (!picked) return;
    setError(null);
    setErrorWorkaround(null);
    const validationError = validatePdfFile(picked);
    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }
    setFile(picked);
  }

  const doImport = useCallback(async (): Promise<void> => {
    if (busy) return;
    setError(null);
    setErrorWorkaround(null);
    setTimedOutInBackground(false);

    if (!file) {
      setError("Velg en PDF-fil.");
      return;
    }

    const runId = ++runIdRef.current;
    const isActive = () => mountedRef.current && runIdRef.current === runId;
    setBusy(true);
    setPhase(null);
    try {
      const { key, uploadUrl } = await requestPdfImportUpload(cookbookId, accessToken);
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": PDF_CONTENT_TYPE },
      });
      if (!putRes.ok) {
        if (isActive()) setError("Opplasting feilet. Prøv igjen.");
        return;
      }
      const { jobId } = await startPdfImport(cookbookId, { uploadedKey: key }, accessToken);

      const imported = await pollPdfImportJob(cookbookId, jobId, accessToken, {
        intervalMs: pollIntervalMs,
        maxAttempts: maxPollAttempts,
        isActive,
        onProgress: (status) => {
          if (isActive()) setPhase(status.phase ?? null);
        },
      });
      if (isActive()) {
        setResult(imported);
        onImported(imported);
      }
    } catch (err) {
      if (err instanceof ImportPollCancelledError) return; // a newer submit / unmount
      if (err instanceof UnauthorizedError) return onSessionExpired();
      if (err instanceof ForbiddenError) return setError("Ingen skrivetilgang.");
      // 402: PDF import isn't in the cookbook's plan (either the upload-url
      // call or the start call can answer this). NOT the same as the 403 above.
      if (err instanceof EntitlementRequiredError) return setError(err);
      if (err instanceof NotFoundError)
        return setError("Importen kunne ikke finnes igjen. Prøv igjen.");
      if (err instanceof ImportJobFailedError) {
        setError(err.serverMessage ?? "Klarte ikke å lese oppskriftene fra PDF-en.");
        setErrorWorkaround(err.errorWorkaround ?? null);
        return;
      }
      if (err instanceof ImportPollTimeoutError) {
        // NOT a failure — the job is very likely still running server-side
        // (see the module docs). Show a neutral notice, never a red error or
        // a retry, and still tell the caller so it refetches the feed /
        // surfaces "Til gjennomgang" for whatever has already landed.
        if (isActive()) {
          setTimedOutInBackground(true);
          onImported({ importedRecipeIds: [], importedCount: 0 });
        }
        return;
      }
      setError(err instanceof Error ? err.message : "Noe gikk galt. Prøv igjen.");
    } finally {
      if (mountedRef.current && runIdRef.current === runId) {
        setBusy(false);
        setPhase(null);
      }
    }
  }, [
    accessToken,
    busy,
    cookbookId,
    file,
    maxPollAttempts,
    onImported,
    onSessionExpired,
    pollIntervalMs,
  ]);

  if (!open) {
    return (
      <>
        <button type="button" className={toggleClassName} data-testid={testId} onClick={openModal}>
          {toggleLabel}
        </button>
        {/* The up-front refusal lives next to the button that was pressed —
            same component, same words as the server's 402. */}
        {isEntitlementDenial(error) && (
          <PremiumNotice denial={error} testId={`${testId}-premium`} />
        )}
      </>
    );
  }

  return (
    <div className="video-import-modal-backdrop" data-testid={`${testId}-backdrop`}>
      <div className="video-import-modal" data-testid={`${testId}-modal`}>
        <h2>Importer fra PDF</h2>

        {timedOutInBackground ? (
          <>
            {/* Deliberately NOT `role="alert"` / not styled as an error —
                this is informational, not a failure. No retry affordance:
                the job is very likely still running server-side, and a
                retry would double-import once it lands its recipes. */}
            <p
              className="pdf-import-notice"
              data-testid={`${testId}-background-notice`}
              role="status"
            >
              Importen tar litt tid og fortsetter i bakgrunnen. Se «Til gjennomgang» om noen
              minutter.
            </p>
            <div className="video-import-actions">
              <button
                type="button"
                className="btn btn-primary"
                data-testid={`${testId}-close`}
                onClick={closeModal}
              >
                Lukk
              </button>
            </div>
          </>
        ) : result ? (
          <>
            <p className="pdf-import-success" data-testid={`${testId}-success`}>
              Importerte {result.importedCount}{" "}
              {result.importedCount === 1 ? "oppskrift" : "oppskrifter"} til gjennomgang.
            </p>
            <div className="video-import-actions">
              <button
                type="button"
                className="btn btn-primary"
                data-testid={`${testId}-close`}
                onClick={closeModal}
              >
                Lukk
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              className="video-import-file-pick"
              data-testid={`${testId}-file-pick`}
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {file ? file.name : "Velg PDF-fil (kokebok med flere oppskrifter)"}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              className="visually-hidden"
              data-testid={`${testId}-file-input`}
              onChange={handleFileChange}
            />

            {busy && (
              <p className="video-import-progress" data-testid={`${testId}-progress`}>
                {phaseLabel(phase)}
              </p>
            )}

            {isEntitlementDenial(error) ? (
              <PremiumNotice denial={error} testId={`${testId}-premium`} />
            ) : typeof error === "string" ? (
              <div className="video-import-error-block">
                <p className="image-error" data-testid={`${testId}-error`} role="alert">
                  {error}
                </p>
                {errorWorkaround && (
                  <p className="video-import-workaround" data-testid={`${testId}-workaround`}>
                    {errorWorkaround}
                  </p>
                )}
              </div>
            ) : null}

            <div className="video-import-actions">
              <button
                type="button"
                className="btn btn-primary"
                data-testid={`${testId}-submit`}
                disabled={busy}
                onClick={() => void doImport()}
              >
                {busy ? "Importerer …" : "Importer"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                data-testid={`${testId}-cancel`}
                disabled={busy}
                onClick={closeModal}
              >
                Avbryt
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
